import {
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoMeasurementUnit,
  isInspectionThicknessCDF,
} from 'types'

export type ShortTermRateInspectionChoice = {
  inspectionId: string
  inspection: GeckoInspection
  cdf: GeckoComponentDataFrame
  label: string
}

export type ShortTermRateInspectionCdfGroup = {
  inspectionId: string
  inspection: GeckoInspection
  cdfs: readonly GeckoComponentDataFrame[]
  label: string
}

export type ShortTermRateSharedResolutionChoice = {
  key: string
  cdf: GeckoComponentDataFrame
  binAreaFeet: number
}

type ShortTermRateInspectionCdfGroupsProps = {
  componentId: string | undefined
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
  inspections: ReadonlyArray<GeckoInspection>
  templateCdf: GeckoComponentDataFrame | undefined
}

export function getShortTermRateInspectionCdfGroups({
  componentId,
  componentDataFrames,
  inspections,
  templateCdf,
}: ShortTermRateInspectionCdfGroupsProps): ShortTermRateInspectionCdfGroup[] {
  if (!componentId) return []

  const inspectionMap = new Map(
    inspections.map((inspection) => [inspection.id, inspection])
  )

  const candidates = componentDataFrames.filter((cdf) => {
    const inspectionId = cdf.tags.inspection
    return (
      cdf.tags.component === componentId &&
      !!inspectionId &&
      isInspectionThicknessCDF(cdf)
    )
  })

  const templateMatchedCandidates =
    templateCdf && isInspectionThicknessCDF(templateCdf)
      ? candidates.filter((cdf) => matchesTemplateCdf(cdf, templateCdf))
      : []

  const poolToUse =
    countDistinctInspections(templateMatchedCandidates) >= 2
      ? templateMatchedCandidates
      : candidates

  return Array.from(groupByInspection(poolToUse).entries())
    .map(
      ([inspectionId, cdfs]): ShortTermRateInspectionCdfGroup | undefined => {
        const inspection = inspectionMap.get(inspectionId)
        if (!inspection) return undefined

        return {
          inspectionId,
          inspection,
          cdfs: cdfs.toSorted(compareCdfsForDisplay),
          label: formatInspectionLabel(inspection),
        }
      }
    )
    .filter((value): value is ShortTermRateInspectionCdfGroup => !!value)
    .toSorted(
      (left, right) =>
        right.inspection.data.date.getTime() -
        left.inspection.data.date.getTime()
    )
}

export function getComparableShortTermRateInspectionCdfGroups({
  componentId,
  componentDataFrames,
  inspections,
  templateCdf,
}: ShortTermRateInspectionCdfGroupsProps): ShortTermRateInspectionCdfGroup[] {
  if (!componentId || !templateCdf || !isInspectionThicknessCDF(templateCdf)) {
    return []
  }

  const inspectionMap = new Map(
    inspections.map((inspection) => [inspection.id, inspection])
  )

  const candidates = componentDataFrames.filter((cdf) => {
    const inspectionId = cdf.tags.inspection
    return (
      cdf.tags.component === componentId &&
      !!inspectionId &&
      isComparableShortTermRateThicknessCdf(cdf, templateCdf)
    )
  })

  return Array.from(groupByInspection(candidates).entries())
    .map(
      ([inspectionId, cdfs]): ShortTermRateInspectionCdfGroup | undefined => {
        const inspection = inspectionMap.get(inspectionId)
        if (!inspection) return undefined

        return {
          inspectionId,
          inspection,
          cdfs: cdfs.toSorted(compareCdfsForDisplay),
          label: formatInspectionLabel(inspection),
        }
      }
    )
    .filter((value): value is ShortTermRateInspectionCdfGroup => !!value)
    .toSorted(
      (left, right) =>
        right.inspection.data.date.getTime() -
        left.inspection.data.date.getTime()
    )
}

export function getSharedResolutionChoices(
  groups: readonly ShortTermRateInspectionCdfGroup[]
): ShortTermRateSharedResolutionChoice[] {
  if (groups.length === 0) return []

  const keyCounts = new Map<string, number>()
  const representativeCdfs = new Map<string, GeckoComponentDataFrame>()

  for (const group of groups) {
    const groupKeys = new Set<string>()

    for (const cdf of group.cdfs) {
      const key = getCdfResolutionKey(cdf)
      if (groupKeys.has(key)) continue

      groupKeys.add(key)
      if (!representativeCdfs.has(key)) {
        representativeCdfs.set(key, cdf)
      }
    }

    for (const key of groupKeys) {
      keyCounts.set(key, (keyCounts.get(key) ?? 0) + 1)
    }
  }

  return Array.from(keyCounts.entries())
    .filter(([, count]) => count === groups.length)
    .map(([key]) => {
      const cdf = representativeCdfs.get(key)
      if (!cdf) return undefined

      return {
        key,
        cdf,
        binAreaFeet: getBinAreaFeet(cdf),
      }
    })
    .filter((value): value is ShortTermRateSharedResolutionChoice => !!value)
    .toSorted((left, right) => left.binAreaFeet - right.binAreaFeet)
}

export function getFinestSharedInspectionCdfs(
  groups: readonly ShortTermRateInspectionCdfGroup[]
): ShortTermRateInspectionChoice[] {
  const finestSharedResolutionKey = getSharedResolutionChoices(groups)[0]?.key

  return groups
    .map((group) => {
      const cdf = finestSharedResolutionKey
        ? selectInspectionCdfByResolutionKey(
            group.cdfs,
            finestSharedResolutionKey
          )
        : selectInspectionCdf(group.cdfs, 'high')

      if (!cdf) return undefined

      return {
        inspectionId: group.inspectionId,
        inspection: group.inspection,
        cdf,
        label: group.label,
      }
    })
    .filter((value): value is ShortTermRateInspectionChoice => !!value)
}

export function getInspectionCdfsForResolutionKey(
  groups: readonly ShortTermRateInspectionCdfGroup[],
  resolutionKey: string
): ShortTermRateInspectionChoice[] {
  return groups
    .map((group) => {
      const cdf = selectInspectionCdfByResolutionKey(group.cdfs, resolutionKey)
      if (!cdf) return undefined

      return {
        inspectionId: group.inspectionId,
        inspection: group.inspection,
        cdf,
        label: group.label,
      }
    })
    .filter((value): value is ShortTermRateInspectionChoice => !!value)
}

export function getCdfResolutionKey(cdf: GeckoComponentDataFrame): string {
  const widthFeet = cdf.data.binning.bin_size.width.convertTo(
    GeckoMeasurementUnit.Feet
  ).num
  const heightFeet = cdf.data.binning.bin_size.height.convertTo(
    GeckoMeasurementUnit.Feet
  ).num

  return `${formatResolutionKeyNumber(widthFeet)}:${formatResolutionKeyNumber(
    heightFeet
  )}`
}

function selectInspectionCdf(
  cdfs: ReadonlyArray<GeckoComponentDataFrame>,
  resolutionMode: 'balanced' | 'high'
): GeckoComponentDataFrame {
  return cdfs.toSorted((left, right) => {
    if (resolutionMode === 'high') {
      const areaComparison = getBinAreaFeet(left) - getBinAreaFeet(right)
      if (areaComparison !== 0) return areaComparison
    }

    return right.generation - left.generation
  })[0]
}

function selectInspectionCdfByResolutionKey(
  cdfs: ReadonlyArray<GeckoComponentDataFrame>,
  resolutionKey: string
): GeckoComponentDataFrame | undefined {
  return cdfs
    .filter((cdf) => getCdfResolutionKey(cdf) === resolutionKey)
    .toSorted((left, right) => right.generation - left.generation)[0]
}

function compareCdfsForDisplay(
  left: GeckoComponentDataFrame,
  right: GeckoComponentDataFrame
): number {
  const areaComparison = getBinAreaFeet(left) - getBinAreaFeet(right)
  if (areaComparison !== 0) return areaComparison

  return right.generation - left.generation
}

function getBinAreaFeet(cdf: GeckoComponentDataFrame): number {
  const widthFeet = cdf.data.binning.bin_size.width.convertTo(
    GeckoMeasurementUnit.Feet
  ).num
  const heightFeet = cdf.data.binning.bin_size.height.convertTo(
    GeckoMeasurementUnit.Feet
  ).num
  return widthFeet * heightFeet
}

function formatResolutionKeyNumber(value: number): string {
  return value.toPrecision(12)
}

function groupByInspection(
  cdfs: ReadonlyArray<GeckoComponentDataFrame>
): Map<string, GeckoComponentDataFrame[]> {
  const grouped = new Map<string, GeckoComponentDataFrame[]>()

  for (const cdf of cdfs) {
    const inspectionId = cdf.tags.inspection
    if (!inspectionId) continue
    const existing = grouped.get(inspectionId)
    if (existing) {
      existing.push(cdf)
    } else {
      grouped.set(inspectionId, [cdf])
    }
  }

  return grouped
}

function countDistinctInspections(
  cdfs: ReadonlyArray<GeckoComponentDataFrame>
): number {
  return new Set(
    cdfs
      .map((cdf) => cdf.tags.inspection)
      .filter((inspectionId): inspectionId is string => !!inspectionId)
  ).size
}

export function isComparableShortTermRateThicknessCdf(
  candidate: GeckoComponentDataFrame,
  templateCdf: GeckoComponentDataFrame
): boolean {
  return (
    isInspectionThicknessCDF(candidate) &&
    candidate.data.params.source.id === templateCdf.data.params.source.id &&
    candidate.data.params.aggregation.id ===
      templateCdf.data.params.aggregation.id &&
    candidate.data.params.measurement_unit.id ===
      templateCdf.data.params.measurement_unit.id
  )
}

function matchesTemplateCdf(
  candidate: GeckoComponentDataFrame,
  templateCdf: GeckoComponentDataFrame
): boolean {
  return isComparableShortTermRateThicknessCdf(candidate, templateCdf)
}

function formatInspectionLabel(inspection: GeckoInspection): string {
  const date = inspection.data.date.toISOString().slice(0, 10)
  const descriptor = [
    inspection.data.vendor,
    inspection.data.name,
    inspection.data.inspection_type,
  ]
    .filter(Boolean)
    .join(' · ')

  return descriptor ? `${date} · ${descriptor}` : `${date} · ${inspection.id}`
}
